import checkoutReducer from "../modules/checkout/reducer";
import deliveryReducer, {
  DELIVERY_ADDRESS,
  DELIVERY_PICKUP,
  normalizeDeliveryCost,
} from "../modules/delivery/reducer";

const VERSION = 1;
const isObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const isId = (value) =>
  (typeof value === "string" && value.length > 0) ||
  (Number.isSafeInteger(value) && value > 0);
const isNumber = (value) => typeof value === "number" && Number.isFinite(value);

function sanitizeState(state) {
  if (!isObject(state)) return undefined;
  const cart = isObject(state.cart)
    ? Object.fromEntries(
        Object.entries(state.cart).filter(
          ([id, qty]) => isId(id) && Number.isSafeInteger(qty) && qty > 0,
        ),
      )
    : {};
  const checkout = {
    ...checkoutReducer(undefined, { type: "@@persist/defaults" }),
  };
  Object.keys(checkout).forEach((key) => {
    if (typeof state.checkout?.[key] === "string")
      checkout[key] = state.checkout[key];
  });
  const delivery = {
    ...deliveryReducer(undefined, { type: "@@persist/defaults" }),
  };
  const savedDelivery = isObject(state.delivery) ? state.delivery : {};
  if ([DELIVERY_ADDRESS, DELIVERY_PICKUP].includes(savedDelivery.type))
    delivery.type = savedDelivery.type;
  ["address", "placeId", "quote"].forEach((key) => {
    if (typeof savedDelivery[key] === "string")
      delivery[key] = savedDelivery[key];
  });
  delivery.cost = normalizeDeliveryCost(savedDelivery.cost);
  if (isNumber(savedDelivery.distance) && savedDelivery.distance >= 0) {
    delivery.distance = savedDelivery.distance;
  }
  if (
    isNumber(savedDelivery.latitude) &&
    Math.abs(savedDelivery.latitude) <= 90
  )
    delivery.latitude = savedDelivery.latitude;
  if (
    isNumber(savedDelivery.longitude) &&
    Math.abs(savedDelivery.longitude) <= 180
  )
    delivery.longitude = savedDelivery.longitude;
  if (isId(savedDelivery.pickup_location_id))
    delivery.pickup_location_id = savedDelivery.pickup_location_id;
  if (!delivery.address) {
    delivery.cost = null;
    delivery.quote = "";
  }
  return { cart, checkout, delivery };
}

export function loadPersistedState(storage) {
  try {
    const raw = (storage ?? window.localStorage).getItem("state");
    if (raw === null) return undefined;
    const parsed = JSON.parse(raw);
    if (!isObject(parsed)) return undefined;
    // Unversioned records are the original Redux snapshot format.
    if (!Object.prototype.hasOwnProperty.call(parsed, "version"))
      return sanitizeState(parsed);
    if (parsed.version !== VERSION) return undefined;
    return sanitizeState(parsed.state);
  } catch (_) {
    return undefined;
  }
}

export function savePersistedState(state, storage) {
  try {
    (storage ?? window.localStorage).setItem(
      "state",
      JSON.stringify({
        version: VERSION,
        state: sanitizeState(state),
      }),
    );
    return true;
  } catch (_) {
    // A full storage should not leave an old cart behind after checkout.
    try {
      (storage ?? window.localStorage).removeItem("state");
    } catch (_) {}
    return false;
  }
}
